
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Play, Info, Bot, Code, CheckCircle, XCircle, Copy, Trash2, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectActionById } from '@/store/slices/actions/actionsSlice';
import { ActionOverviewTab } from './action-detail/ActionOverviewTab';
import { ActionParametersTab } from './action-detail/ActionParametersTab';
import { ActionTestingTab } from './action-detail/ActionTestingTab';
import { ActionConnectionsTab } from './action-detail/ActionConnectionsTab';
import { ActionApiConfigTab } from './action-detail/ActionApiConfigTab';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function ActionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const action = useAppSelector(state => selectActionById(state, id!));

  if (!action) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="p-8 text-center">
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">Action not found</h2>
              <p className="text-muted-foreground mb-6">The action you're looking for doesn't exist or may have been deleted.</p>
              <Button onClick={() => navigate('/home/automation/ai/action-center')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Action Center
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleDuplicate = () => {
    // Implementation for duplicating action
    console.log('Duplicate action:', action.id);
  };

  const handleDelete = () => {
    // Implementation for deleting action
    console.log('Delete action:', action.id);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/home/automation/ai/action-center')}
              className="mr-4 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Action Center
            </Button>
          </div>
          
          <Card className="border-0 shadow-none bg-card">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                    <Code className="h-7 w-7 text-primary" />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <h1 className="text-3xl font-bold text-foreground mb-2">{action.name}</h1>
                      <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">{action.description}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <Badge 
                        variant={action.enabled ? 'default' : 'secondary'}
                        className={action.enabled ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-600 border-gray-200'}
                      >
                        {action.enabled ? (
                          <CheckCircle className="h-3 w-3 mr-1.5" />
                        ) : (
                          <XCircle className="h-3 w-3 mr-1.5" />
                        )}
                        {action.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline" className="font-mono text-xs bg-muted/50">
                        {action.method}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Created {new Date(action.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-2" />
                    Edit Action
                  </Button>
                  <Button size="sm" className="bg-primary hover:bg-primary/90">
                    <Play className="h-4 w-4 mr-2" />
                    Test Action
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={handleDuplicate}>
                          <Copy className="mr-2 h-4 w-4" />
                          Duplicate
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <Card className="border-0 shadow-sm bg-card">
            <TabsList className="w-full h-auto p-1 bg-muted/30 rounded-lg">
              <TabsTrigger 
                value="overview" 
                className="flex-1 h-11 px-6 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <Info className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="api-config" 
                className="flex-1 h-11 px-6 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <Code className="h-4 w-4 mr-2" />
                API Configuration
              </TabsTrigger>
              <TabsTrigger 
                value="parameters" 
                className="flex-1 h-11 px-6 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <Settings className="h-4 w-4 mr-2" />
                Parameters
              </TabsTrigger>
              <TabsTrigger 
                value="testing" 
                className="flex-1 h-11 px-6 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <Play className="h-4 w-4 mr-2" />
                Testing
              </TabsTrigger>
              <TabsTrigger 
                value="connections" 
                className="flex-1 h-11 px-6 rounded-md text-sm font-medium transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                <Bot className="h-4 w-4 mr-2" />
                Connections
              </TabsTrigger>
            </TabsList>
          </Card>

          <div className="space-y-6">
            <TabsContent value="overview" className="mt-0">
              <ActionOverviewTab action={action} />
            </TabsContent>

            <TabsContent value="api-config" className="mt-0">
              <ActionApiConfigTab action={action} />
            </TabsContent>

            <TabsContent value="parameters" className="mt-0">
              <ActionParametersTab action={action} />
            </TabsContent>

            <TabsContent value="testing" className="mt-0">
              <ActionTestingTab action={action} />
            </TabsContent>

            <TabsContent value="connections" className="mt-0">
              <ActionConnectionsTab action={action} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
